/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { Component, computed, inject, OnDestroy, OnInit, signal, input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { ApplicationService } from '../../application.service';
import { AuthService } from '../../auth.service';
import { CustomerSelectionService } from '../../customer-selection.service';
import { GlobalEventService } from '../../global-event.service';
import { Application } from '../../models/application/application.interface';
import { ThemeDataType } from '../../models/customer/theme/themeDataType.enum';
import { AuthUser } from '../../models/user/auth-user.interface';
import { Tenant } from '../../models/customer/tenant.interface';
import { StartupService } from '../../startup.service';
import { ThemeService } from '../../theme.service';
import { MenuOption } from '../../models/menu-option.interface';
import { ApplicationId } from './../../application-id.enum';
import { SubrogationService } from './../../subrogation/subrogation.service';
import { TENANT_SELECTION_URL_CONDITION, TenantSelectionService } from './../../tenant-selection.service';
import { MenuOverlayService } from './menu/menu-overlay.service';
import { SelectTenantDialogComponent } from './select-tenant-dialog/select-tenant-dialog.component';
import { MatToolbar } from '@angular/material/toolbar';
import { SelectSiteComponent } from './select-site/select-site.component';
import { SelectLanguageComponent } from './select-language/select-language.component';
import { ItemSelectComponent } from './item-select/item-select.component';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { UserPhotoComponent } from './user-photo/user-photo.component';
import { MatMiniFabButton } from '@angular/material/button';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'vitamui-common-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    MatToolbar,
    SelectSiteComponent,
    SelectLanguageComponent,
    ItemSelectComponent,
    MatMenuTrigger,
    UserPhotoComponent,
    MatMenu,
    MatMenuItem,
    RouterLink,
    MatMiniFabButton,
    TranslatePipe,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly subrogationService = inject(SubrogationService);
  private readonly startupService = inject(StartupService);
  private readonly menuOverlayService = inject(MenuOverlayService);
  private readonly authService = inject(AuthService);
  private readonly tenantSelectionService = inject(TenantSelectionService);
  private readonly customerSelectionService = inject(CustomerSelectionService);
  private readonly themeService = inject(ThemeService);
  private readonly matDialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly applicationService = inject(ApplicationService);
  private readonly globalEventService = inject(GlobalEventService);

  readonly hasLangSelection = input(false);

  /** TODO : rooting /account in portal module => move to header module */
  public trustedInlineLogoUrl: SafeUrl;
  public hasSiteSelection = false;
  public portalUrl: string;
  public selectedTenant = signal<MenuOption>(undefined);
  public selectedCustomer = signal<MenuOption>(undefined);
  public customers = signal<MenuOption[]>(undefined);
  public tenants: MenuOption[];
  public appTenants = signal<MenuOption[]>(undefined);

  public currentUser = toSignal(this.authService.user$, { initialValue: this.authService.user as AuthUser });
  public hasAccountProfile = computed(
    () => this.currentUser()?.profileGroup?.profiles?.some((profile) => profile.applicationName === ApplicationId.ACCOUNTS_APP) ?? false,
  );
  public hasTenantSelection = toSignal(this.applicationService.hasTenantList(), { initialValue: false });
  public hasCustomerSelection = toSignal(
    this.globalEventService.pageEvent.pipe(
      switchMap((appId: string) => this.applicationService.getAppById(appId).pipe(map((app: Application) => !!app?.hasCustomerList))),
    ),
    { initialValue: false },
  );
  public headerLogoUrl = toSignal(
    this.authService.user$.pipe(switchMap((user: AuthUser) => this.themeService.getData$(user, ThemeDataType.HEADER_LOGO))),
    { initialValue: null as SafeResourceUrl },
  );

  private currentAppId: ApplicationId;
  private readonly destroyer$ = new Subject<void>();

  ngOnInit() {
    this.portalUrl = this.startupService.getPortalUrl();
    this.hasSiteSelection = this.startupService.getHasSiteSelection();
    this.tenants = this.tenantSelectionService.getTenants().map((tenant: Tenant) => {
      return { value: tenant, label: tenant.name };
    });

    // Open the select default tenant dialog if no default tenant identifier defined or if default value not in selected tenant list
    const dialogConfig = SelectTenantDialogComponent.SELECT_TENANT_DIALOG_CONFIG;
    dialogConfig.data = { tenants: this.tenants };
    this.tenantSelectionService
      .getLastTenantIdentifier$()
      .pipe(takeUntil(this.destroyer$))
      .subscribe((value: number) => {
        if (!value || this.tenants.filter((option) => option.value.identifier === value).length === 0) {
          this.matDialog
            .open(SelectTenantDialogComponent, dialogConfig)
            .beforeClosed()
            .subscribe((selectedTenant: Tenant) => {
              this.tenantSelectionService
                .saveTenantIdentifier(selectedTenant.identifier)
                .toPromise()
                .then(() => {
                  this.tenantSelectionService.setSelectedTenant(selectedTenant);
                });
            });
        }
      });

    this.initTenantSelection();

    this.tenantSelectionService.currentAppId$.pipe(takeUntil(this.destroyer$)).subscribe((appIdentifier: ApplicationId) => {
      this.currentAppId = appIdentifier;
      this.initCurrentAppTenants(this.currentAppId);
      this.initCustomerSelection(this.currentAppId);
    });
  }

  ngOnDestroy() {
    this.destroyer$.next();
    this.destroyer$.complete();
  }
  public updateTenant(tenant: MenuOption): void {
    this.tenantSelectionService.setSelectedTenant(tenant.value);
    this.changeTenant(tenant.value?.identifier);
  }

  public updateCustomer(customer: MenuOption): void {
    this.customerSelectionService.setCustomerId(customer.value);
  }

  public enabledSubrogation(): void {
    this.subrogationService.checkSubrogation();
  }

  public logout(): void {
    this.authService.logout();
  }

  public openMenu(): void {
    this.menuOverlayService.open();
  }

  /**
   * Init tenant selection feature & listeners if the current opened application requires it.
   */
  private initTenantSelection(): void {
    if (this.router.events) {
      this.router.events.pipe(takeUntil(this.destroyer$)).subscribe((data: any) => {
        if (!this.hasTenantSelection()) return;
        if (data?.snapshot?.params) {
          const tenantIdentifier = +data.snapshot.params.tenantIdentifier;
          if (tenantIdentifier) {
            this.tenantSelectionService.setSelectedTenantByIdentifier(tenantIdentifier);
            this.tenantSelectionService
              .getSelectedTenant$()
              .pipe(takeUntil(this.destroyer$))
              .subscribe((tenant: Tenant) => {
                if (tenant.identifier !== tenantIdentifier) {
                  this.changeTenant(tenant.identifier);
                }
              });
          }
        }
      });
    }

    // Subscribe to route changes
    this.router.events.pipe(takeUntil(this.destroyer$)).subscribe((data: any) => {
      if (data?.snapshot?.params) {
        const tenantIdentifier = +data.snapshot.params.tenantIdentifier;
        // Subcribe to active tenant changes
        if (!!tenantIdentifier) {
          this.tenantSelectionService.setSelectedTenantByIdentifier(tenantIdentifier);
        }
      }
    });

    // Subcribe to active tenant changes
    this.tenantSelectionService
      .getSelectedTenant$()
      .pipe(takeUntil(this.destroyer$))
      .subscribe((tenant: Tenant) => {
        if (!this.selectedTenant()) {
          this.selectedTenant.set({ value: tenant, label: tenant.name });
        } else {
          if (this.selectedTenant().value.identifier !== tenant.identifier) {
            this.selectedTenant.set({ value: tenant, label: tenant.name });
            this.tenantSelectionService.saveSelectedTenant(tenant).toPromise();
          }
        }
      });

    this.initLastTenantIdentifier();
  }

  /**
   * Init last tenant only if there is no active tenant.
   * The subscription will stop when a tenant is set as active.
   */
  private initLastTenantIdentifier() {
    this.tenantSelectionService
      .getLastTenantIdentifier$()
      .pipe(takeUntil(this.tenantSelectionService.getSelectedTenant$()), takeUntil(this.destroyer$))
      .subscribe((identifier: number) => {
        const lastTenant = this.tenants.find((option: MenuOption) => option.value.identifier === identifier);
        if (!this.selectedTenant() && lastTenant) {
          this.tenantSelectionService.setSelectedTenant(lastTenant.value);
        }
      });
  }

  private initCurrentAppTenants(appIdentifier: string): void {
    if (appIdentifier === ApplicationId.PORTAL_APP) {
      this.appTenants.set(this.tenants);
    } else {
      this.appTenants.set(
        this.applicationService.getApplicationTenants(appIdentifier).map((tenant: Tenant) => {
          return { value: tenant, label: tenant.name };
        }),
      );
    }
  }

  /**
   * Init customer selection feature & listeners if the current opened application requires it.
   */
  private initCustomerSelection(appIdentifier: string): void {
    if (appIdentifier) {
      this.applicationService.getAppById(appIdentifier).subscribe((currentApp: Application) => {
        if (currentApp?.hasCustomerList) {
          this.customerSelectionService
            .getCustomers$()
            .pipe(takeUntil(this.destroyer$))
            .subscribe((customers: MenuOption[]) => {
              this.customers.set(customers);
            });

          this.customerSelectionService
            .getSelectedCustomerId$()
            .pipe(takeUntil(this.destroyer$))
            .subscribe((identifier: string) => {
              const customers = this.customers();
              if (customers && (!this.selectedCustomer() || this.selectedCustomer().value !== identifier)) {
                this.selectedCustomer.set(customers.find((value) => value.value === identifier));
                this.globalEventService.customerEvent.next(this.selectedCustomer().value);
              }
            });
        }
      });
    }
  }

  private changeTenant(tenantIdentifier: number): void {
    this.router.navigate([this.route.firstChild.snapshot.routeConfig.path + TENANT_SELECTION_URL_CONDITION, tenantIdentifier], {
      relativeTo: this.route,
    });
  }
}
